import React from "react";

import {
  Box,
  Button,
  Center,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  VStack
} from "@chakra-ui/react";

import { FaCloudUploadAlt } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";

import { getFileSize } from "@/util/helpers";

interface UploadPDFProps {
  isOpen: boolean;
  onClose: () => void;
  isUploading: boolean;
  handleSubmitFiles: (event: React.FormEvent) => void;
  handleUploadFiles: (event: React.ChangeEvent<HTMLInputElement>) => void;
  files: Array<{ id: string; file: File }>;
  setFiles: (files: Array<{ id: string; file: File }>) => void;
}

export const UploadPDF: React.FC<UploadPDFProps> = ({
  isOpen,
  onClose,
  isUploading,
  handleSubmitFiles,
  handleUploadFiles,
  files,
  setFiles
}) => {
  const removeFile = (fileId: string) => {
    setFiles(files.filter(({ id }) => id !== fileId));
  };

  const MAX_FILES = 5;

  return (
    <Modal
      size="xl"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      motionPreset="slideInBottom"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent w={618} pt={10} pb={8} px={10}>
        <ModalCloseButton top={8} right={8} />
        <Box w="full">
          <Text
            fontWeight={600}
            color="secondary.600"
            fontSize="lg"
            pt={0}
            pb={2}
          >
            Convert to CSV
          </Text>
          <Text fontWeight={500} fontSize={14}>
            Upload Bank Statements and convert to CSV. You will be able to
            access these in CSV and PDF in the documents sections.
          </Text>

          <Stack
            as="form"
            mt={5}
            onSubmit={handleSubmitFiles}
            opacity={isUploading ? 0.5 : 1}
            pointerEvents={isUploading ? "none" : "initial"}
            rounded={6}
            spacing={3}
            w="full"
            bg="white"
          >
            <Stack spacing={4}>
              {files.length < MAX_FILES && (
                <Center
                  pos="relative"
                  flexShrink={0}
                  cursor="pointer"
                  pb={6}
                  h={{ base: "full", lg: 270 }}
                  flexDir="column"
                  justifyContent="space-between"
                  border="1px dashed"
                  borderColor="gray.400"
                  w="full"
                  rounded={20}
                >
                  <Input
                    as="input"
                    zIndex={5}
                    opacity={0}
                    multiple
                    w="full"
                    h="full"
                    pos="absolute"
                    top={0}
                    cursor="pointer"
                    type="file"
                    onChange={handleUploadFiles}
                    accept="application/pdf"
                  />

                  <Box />
                  <VStack>
                    <Icon
                      as={FaCloudUploadAlt}
                      fontSize={70}
                      color="primary.500"
                    />
                    <VStack spacing={0}>
                      <Text fontSize="sm" color="gray.600" fontWeight={600}>
                        Drop files here or
                      </Text>
                      <Text fontSize="sm" color="blue.500" fontWeight={600}>
                        Browse
                      </Text>
                    </VStack>
                  </VStack>

                  <VStack spacing={2}>
                    <Text fontSize="xs" color="gray.600" fontWeight={600}>
                      Format: PDF
                    </Text>
                    <Text fontSize="xs" color="gray.600" fontWeight={600}>
                      {MAX_FILES} files upload limit at a time
                    </Text>
                  </VStack>
                </Center>
              )}
              {!!files.length && (
                <Stack pb={6}>
                  {files.map(({ id, file }) => (
                    <HStack
                      rounded={10}
                      border="1px solid"
                      borderColor="gray.300"
                      key={id}
                      px={5}
                      w="full"
                      py={1}
                      spacing={4}
                    >
                      <Stack spacing={1} w="full">
                        <HStack justify="space-between">
                          <Text color="black" fontWeight={500} fontSize={12}>
                            {file.name}
                          </Text>
                        </HStack>
                        <Stack w="full" maxW={200} spacing={1}>
                          <Box bg="green.400" w="full" rounded="full" h={1.5} />
                          <HStack
                            fontSize="xs"
                            color="gray.500"
                            justify="space-between"
                          >
                            <Text>{getFileSize(file.size)}</Text>
                            <Text>Ready</Text>
                          </HStack>
                        </Stack>
                      </Stack>
                      <IconButton
                        aria-label="remove"
                        onClick={() => removeFile(id)}
                        variant="ghost"
                        icon={<FiTrash2 />}
                        size="sm"
                      />
                    </HStack>
                  ))}
                </Stack>
              )}
            </Stack>

            <HStack justify="flex-end" spacing={3} pt={0}>
              <Button h={9} size="sm" variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button
                isDisabled={!files.length}
                loadingText="Uploading..."
                h={9}
                size="sm"
                w={167}
                isLoading={isUploading}
                type="submit"
              >
                Convert
              </Button>
            </HStack>
          </Stack>
        </Box>
      </ModalContent>
    </Modal>
  );
};
